<template>
    <div id="bancai" class="bancai-page livingRoomWap">
        <section class="hero" :class="{ jianbianbanner: true }" ref="banner">
            <div class="spaceCustomization_img homeDecoration productkjdz"  ref="homeDecoration">
                <div class="spaceCustomization">
                    <picture>
                        <source media="(max-width: 768px)" :srcset="heroImageMobile" />
                        <img :src="heroImageDesktop" alt="" />
                    </picture>
                </div>
                <div class="hero-overlay">
                    <div class="cn">进口岩板</div>
                    <div class="en" ref="enText">Imported rock slab</div>
                </div>
                <div class="hero-sides" ref="heroSides">
                    <div class="side-link left" @click="goLeft">大家居</div>
                    <div class="side-link right" @click="goRight">门墙系统</div>
                </div>
            </div>
        </section>

        <section class="intro">
            <!-- <div class="shutiao"></div> -->
            <div class="intro-title">意大利进口岩板系列</div>
            <p class="intro-desc">
                设计以材质匠筑生活美学，空间于实用与诗意间平衡，岩板以自然肌理与多元形<br class="desktop-br"/>态铺陈艺术底色，恰然成为空间里的「艺术注脚」。
            </p>
        </section>

        <section class="items">
            <div class="grid">
                <div v-for="item in items" :key="item.id || item.name" class="card" @click="openItem(item)">
                    <div class="thumb-wrap">
                        <img class="thumb" :src="item.image || item.img || item.url || placeholder" :alt="item.name || item.title || 'item'" />
                        <div class="hover-overlay">
                            <div class="hover-content">
                                <div class="plus-icon">+</div>
                                <div class="hover-text">更多详情</div>
                            </div>
                        </div>
                    </div>
                    <div class="meta">
                        <p class="name">{{ item.name || item.title || '未命名' }}</p>
                        <!-- <p class="sub" v-if="item.subtitle || item.spec">{{ item.subtitle || item.spec }}</p> -->
                    </div>
                </div>
            </div>
            <div v-if="!loading && items.length === 0" class="empty">暂无数据</div>
        </section>
    </div>
</template>

<script setup>
import { onMounted, ref, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { getBanCai } from '@/api/api'
import { testData } from './testData.js'

const router = useRouter()
const items = ref([])
const loading = ref(false)
const placeholder = ref(require('@/components/img/btn.png'))
const heroImageDesktop = ref('https://vifapicter.oss-cn-guangzhou.aliyuncs.com/images/bancaiBG.png')
const heroImageMobile = ref('https://vifapicter.oss-cn-guangzhou.aliyuncs.com/images/m_bancaiBG.png')

const banner = ref(null)
const enText = ref(null)
const heroSides = ref(null)

// 规范化文件 URL，支持 http(s)、/开头与相对路径
const resolveUrl = (url) => {
    if (!url) return ''
    if (typeof url !== 'string') return ''
    if (/^https?:\/\//i.test(url)) return url
    if (url.startsWith('/')) return `${window.location.origin}${url}`
    return `${window.location.origin}/${url}`
}

const normalizeItems = (data) => {
    if (!data) return []
    if (Array.isArray(data)) return data
    if (Array.isArray(data.list)) return data.list
    if (Array.isArray(data.records)) return data.records
    // 适配后端返回 { bancaiList: [...] }
    if (Array.isArray(data.bancaiList)) return data.bancaiList
    return []
}

const fetchItems = async () => {
    loading.value = true
    try {
        const res = await getBanCai()
        console.log("getBanCai:", res)
        // 仅选择对象作为原始数据体，避免把字符串/数字 result 当作数据体
        const rawCandidate = (res && (res.data || res.body)) || res
        const raw = (rawCandidate && typeof rawCandidate === 'object') ? rawCandidate : res
        const apiData = normalizeItems(raw)
        
        if (apiData && apiData.length > 0) {
            // 使用接口数据，映射到正确的字段
            const mappedData = apiData.map((it, idx) => ({
                id: it.id || idx,
                name: it.name || '未命名产品',
                image: resolveUrl(it.mainImage || it.image) || require('@/common/image/allListbanner.png'),
                subtitle: (() => {
                    let count = 0
                    try {
                        if (it.styles) {
                            if (typeof it.styles === 'string') {
                                const arr = JSON.parse(it.styles)
                                if (Array.isArray(arr)) count = arr.length
                            } else if (Array.isArray(it.styles)) {
                                count = it.styles.length
                            }
                        }
                    } catch (e) {
                        count = 0
                    }
                    return `${count}个饰面`
                })(),
                sortOrder: it.sortOrder || 0 // 保留原始排序字段
            }))
            
            // 根据 sortOrder 字段从小到大排序
            items.value = mappedData.sort((a, b) => {
                const sortA = a.sortOrder || 0
                const sortB = b.sortOrder || 0
                return sortA - sortB
            })
        } else {
            // 接口数据为空时使用测试数据
            console.log('接口数据为空，使用测试数据')
            items.value = testData
        }
    } catch (e) {
        console.warn('获取岩板数据失败，使用测试数据', e)
        // 接口失败时使用测试数据
        items.value = testData
    } finally {
        loading.value = false
        // 数据与图片加载后再尝试对齐（下一帧）
        requestAnimationFrame(() => alignSidesWithEn())
    }
}

const alignSidesWithEn = () => {
    try {
        const heroEl = banner.value
        const enEl = enText.value
        const sidesEl = heroSides.value
        if (!heroEl || !enEl || !sidesEl) return
        const heroRect = heroEl.getBoundingClientRect()
        const enRect = enEl.getBoundingClientRect()
        // 让左右横线容器的底边与英文底部对齐
        const sidesHeight = sidesEl.offsetHeight || 0
        const baseline = Math.round(enRect.bottom - heroRect.top)
        const topForBottomAlign = baseline - sidesHeight + 1 // +1 微调到描边下沿
        sidesEl.style.top = `${topForBottomAlign}px`
    } catch (err) {
        // 忽略对齐异常
    }
}

const onResize = () => {
    alignSidesWithEn()
}

const goLeft = () => {
    try {
        router.push({ path: '/homeProducts' })
    } catch {
        console.warn('路由不存在，作为占位')
    }
}
const goRight = () => {
    try {
        router.push({ path: '/doorwallsystem' })
    } catch {
        console.warn('路由不存在，作为占位')
    }
}
const openItem = (item) => {
    router.push({ path: '/bancaiDetails', query: { id: item.id } })
}

onMounted(() => {
    fetchItems()
    window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
})
</script>

<style scoped>
.bancai-page {
    width: 100%;
    color: #333;
    /* font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; */
}
/* 顶部 banner 采用与 productCenter 一致的容器类，绝对定位的覆盖元素仍使用本地样式 */
.hero { position: relative; }
.hero-image { width: 100%; display: block; }
.hero-overlay {
    position: absolute;
    left: 50%;
    top: calc(50% - 100px);
    transform: translate(-50%, -50%);
    text-align: center;
    color: #fff;
}
.hero-overlay .cn { color: rgb(255, 255, 255); cursor: pointer; display: block; font-size: 2.5em !important; line-height: 1; text-align: center; text-size-adjust: 100%;font-weight: 220; unicode-bidi: isolate; letter-spacing: 10px; }
/* .hero-overlay .cn { color: rgb(255, 255, 255); cursor: pointer; display: block; font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif; font-size: 2.5em !important; font-weight: 100; line-height: 1; text-align: center; text-size-adjust: 100%; unicode-bidi: isolate; } */

.hero-overlay .en { margin-top: 6px; font-size: 36px !important; opacity: 0.9; font-weight: 220;}
.hero-sides {
    position: absolute;
    left: 0; right: 0;
    /* 由脚本设置 top，使其与英文底部对齐 */
    top: auto;
    transform: none;
    display: flex; justify-content: space-between;
    padding: 0 24px;
    /* 收拢到中间（稍微放宽一点） */
    max-width: 1200px;
    margin: 0 auto;
    width: 80%;
}
.side-link { 
    background: transparent; 
    color: #fff; 
    border: none; 
    padding: 15px 0; 
    cursor: pointer; 
    position: relative; 
    font-size: 16px;
    display: inline-block;

    /* 文字宽度下划线 */
    /* border-bottom: 1px solid rgba(255,255,255,.95); */
}
/* 左侧：从文字左侧开始向右延伸（下划线） */
.side-link.left::after{
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    height: 0.3px;
    width: 200px;
    background: #fff;
}
/* 右侧：从文字右侧开始向左延伸（下划线） */
.side-link.right::before{
    content: "";
    position: absolute;
    bottom: -1px;
    right: 0;
    height: 0.3px;
    width: 200px;
    background: #fff;
}

.intro { max-width: 1040px; margin: 80px auto 60px; padding: 0 16px; text-align: center; }
.intro-title {color: #4c413d; font-size: 50px;  font-weight: 100; letter-spacing: 1px; }
/* .intro-title {color: #4c413d; font-size: 50px; font-family: "Lantinghei SC Extralight", "Lantinghei SC Demibold", "Lantinghei SC Heavy", "Lantinghei SC", "PingFang SC", "Helvetica Neue", Arial, sans-serif; font-weight: 100; letter-spacing: 1px; } */
/* 标题上方短橙色竖线（默认显示，居中） */
.intro-title::before {
    content: "";
    display: block;
    width: 1px;
    height: 50px;
    background: rgb(240, 133, 24);
    margin: 0 auto 50px;
    border-radius: 1px;
}

.intro-desc { margin-top: 50px; line-height: 1.8; color: #4c413d; } 

.items { max-width: 1400px; margin: 16px auto 64px; padding: 0 16px; }
.grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 30px 20px; }
.card { background: #fff; border: none; transition: transform .2s ease; cursor: pointer; }
.card:hover { transform: translateY(-2px); }
.thumb-wrap { 
    width: 100%; 
    padding-top: 200%; 
    position: relative; 
    background: #f7f7f7; 
    border: 1px solid #ddd; 
    overflow: hidden;
}
.thumb { position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: cover; }

/* 悬浮遮罩层 */
.hover-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 2;
}

.card:hover .hover-overlay {
    opacity: 1;
}

.hover-content {
    text-align: center;
    color: #fff;
}

.plus-icon {
    font-size: 24px;
    font-weight: 300;
    margin-bottom: 15px;
    line-height: 1;
}

.hover-text {
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 1px;
}
.shutiao{
    background-attachment: scroll;
background-clip: border-box;
background-color:rgb(240, 133, 24);
background-image:none;
background-origin:padding-box;
background-position-x:0%;
background-position-y:0%;
background-repeat:repeat;
background-size:auto;
}
.meta { padding: 12px 0; text-align: left; }
.name { font-size: 16px; color: #333; font-weight: 500; margin-bottom: 5px; }
.sub { font-size: 13px; color: #666; }

@media (min-width: 1200px) {
    .grid { grid-template-columns: repeat(6, 1fr); }
    .intro-title { font-weight: 100; }
}
@media (min-width: 992px) and (max-width: 1199.98px) {
    .grid { grid-template-columns: repeat(5, 1fr); }
    .intro-title { font-weight: 200; }
}
@media (min-width: 768px) and (max-width: 991.98px) {
    .grid { grid-template-columns: repeat(4, 1fr); }
}

@media (max-width: 768px) {
    .grid { 
        grid-template-columns: repeat(5, 1fr); 
        gap: 16px 14px; 
    }
    /* 1:2 高宽比 */
    .thumb-wrap { padding-top: 200%; }
    .meta { padding: 10px 0; text-align: left; }
    .name { font-size: 20px; }
    .sub { font-size: 10px; }
    /* 强制隐藏横幅两侧按钮 */
    .hero-sides { display: none !important; }
    /* 小屏横幅位置不受桌面上移影响 */
    .hero-overlay { top: 50%; }
    /* 描述字体缩小 */
    .intro-desc { font-size: 20px; line-height: 1.7; width: 85%; margin-left: auto; margin-right: auto; }
    /* 小屏隐藏手动换行 */
    .intro-desc br.desktop-br { display: none; }
    /* 标题在小屏去掉加粗，减少字间距 */
    .intro-title { font-weight: 300; letter-spacing: 0; }
    /* 小屏隐藏标题上方竖线 */
    .intro-title::before { display: none; }
    /* 标题上方短茶色竖线（更长、更浅、间距更大） */
    /* .intro-title::before {
        content: "";
        display: block;
        width: 3px;
        height: 32px;
        background: #B98C66;
        margin: 0 auto 16px;
        border-radius: 2px;
    } */
    /* 小屏横幅中文主标题更大 */
    .hero-overlay .cn { font-size: 52px !important; line-height: 1.2; font-weight: 300; }
    /* 小屏横幅英文副标题细体 */
    .hero-overlay .en { font-weight: 300; }
}

@media (max-width: 480px) {
    .grid { 
        grid-template-columns: repeat(4, 1fr); 
        gap: 14px 12px; 
    }
    /* 1:2 高宽比 */
    .thumb-wrap { padding-top: 200%; }
    .meta { padding: 8px 0; text-align: left; }
    .name { font-size: 20px; }
    .sub { font-size: 9px; }
    /* 强制隐藏横幅两侧按钮 */
    .hero-sides { display: none !important; }
    /* 小屏横幅位置不受桌面上移影响 */
    .hero-overlay { top: 50%; }
    /* 描述字体更小 */
    .intro-desc { font-size: 20px; line-height: 1.6; width: 85%; margin-left: auto; margin-right: auto; }
    /* 超小屏隐藏手动换行 */
    .intro-desc br.desktop-br { display: none; }
    /* 超小屏横幅中文主标题更大一点 */
    .hero-overlay .cn { font-size: 48px !important; line-height: 1.2; font-weight: 300; }
    /* 超小屏横幅英文副标题细体 */
    .hero-overlay .en { font-weight: 300; }
}

/* 覆盖导入的CSS文件中的固定高度设置 */
.livingRoomWap .homeDecoration {
    height: auto !important;
}

.livingRoomWap .homeDecoration img {
    height: auto !important;
    width: 100%;
    object-fit: cover;
}
</style>

<style>
@import '../../../common/styles/productCenter.css';
</style>